// SIMO-CROSS-DEVICE-SYNC-1 — Web/Desktop live-sync client.
//
// Consumes the owner-scoped SSE stream (`/api/events/stream`) and reduces events
// into a small sync-state. Actions still go through REST; this is read/refresh
// only. On every (re)connect the caller does a full snapshot resync, so any
// events missed while disconnected are invisible.

export type SyncEventType =
  | "approval.created"
  | "approval.decided"
  | "device.changed"
  | "conversation.updated"
  | "message.created"
  // SIMO-AGENT-ROOM-LIVE-1 — deterministischer Live-Push (kein Polling).
  | "agent.room.updated";

export interface SyncEvent {
  v: 1;
  type: SyncEventType;
  ts: string;
  data: Record<string, unknown>;
}

export interface SyncState {
  pendingApprovalIds: string[];
  deviceTrust: Record<string, boolean>;
  /** Flips true when a conversation/message event arrives; caller resyncs then resets. */
  conversationsDirty: boolean;
}

export const initialSyncState: SyncState = {
  pendingApprovalIds: [],
  deviceTrust: {},
  conversationsDirty: false,
};

export function applyOwnerEvent(state: SyncState, event: SyncEvent): SyncState {
  switch (event.type) {
    case "approval.created": {
      const id = String(event.data.id);
      if (state.pendingApprovalIds.includes(id)) return state;
      return { ...state, pendingApprovalIds: [...state.pendingApprovalIds, id] };
    }
    case "approval.decided": {
      const id = String(event.data.id);
      return { ...state, pendingApprovalIds: state.pendingApprovalIds.filter((x) => x !== id) };
    }
    case "device.changed":
      return {
        ...state,
        deviceTrust: { ...state.deviceTrust, [String(event.data.id)]: event.data.trusted === true },
      };
    case "conversation.updated":
    case "message.created":
      return { ...state, conversationsDirty: true };
    default:
      return state;
  }
}

export interface EventStreamHandle {
  close: () => void;
}

const RECONNECT_BACKOFF_MS = 3000;
const OWNER_EVENTS_STREAM_ENDPOINT = "/api/events/stream";

function parseRequestId(headers: Headers, bodyText: string): string | null {
  const headerId = headers.get("x-request-id") ?? headers.get("x-simo-request-id");
  if (headerId) return headerId;
  try {
    const parsed = JSON.parse(bodyText) as { requestId?: unknown };
    return typeof parsed.requestId === "string" ? parsed.requestId : null;
  } catch {
    return null;
  }
}

function parseErrorCode(bodyText: string): string | null {
  try {
    const parsed = JSON.parse(bodyText) as { error?: unknown; code?: unknown };
    const code = parsed.error ?? parsed.code;
    return typeof code === "string" ? code : null;
  } catch {
    return null;
  }
}

export function ownerEventStreamErrorMessage(
  path: string,
  status: number,
  statusText: string,
  bodyText: string,
  requestId: string | null,
): string {
  const code = parseErrorCode(bodyText);
  const prefix =
    status === 401 && (code === "missing_session" || code === "invalid_session")
      ? `Owner-Session fehlt oder ist abgelaufen. Bitte neu anmelden und danach Live-Daten erneut laden. Endpoint: ${path}.`
      : status === 401 && code === "unauthorized"
        ? `API-Zugriff nicht freigegeben. Der Server hat den Request vor der Owner-Session-Pruefung abgelehnt. Endpoint: ${path}.`
        : status === 403
          ? `Owner-Freigabe erforderlich. Deine Session ist vorhanden, aber dieser Live-Stream braucht Owner/Admin-Rechte. Endpoint: ${path}.`
          : `Live-Stream nicht verfuegbar. Endpoint: ${path}. HTTP ${status} ${statusText || "Fehler"}.`;
  return requestId ? `${prefix} Request-ID ${requestId}` : prefix;
}

export function parseSseChunk(buffer: string): { events: SyncEvent[]; rest: string } {
  const events: SyncEvent[] = [];
  const parts = buffer.split("\n\n");
  const rest = parts.pop() ?? "";
  for (const block of parts) {
    const dataLine = block.split("\n").find((line) => line.startsWith("data:"));
    if (!dataLine) continue;
    try {
      events.push(JSON.parse(dataLine.slice(5).trim()) as SyncEvent);
    } catch {
      /* ignore malformed frame */
    }
  }
  return { events, rest };
}

/**
 * Opens the SSE stream. `onOpen` fires on (re)connect — the caller MUST do a
 * full snapshot resync there. `onEvent` fires per live event. The browser
 * EventSource cannot set fetch credentials explicitly across our test harness,
 * so this uses fetch with same-origin cookie credentials and reconnects with a
 * small fixed backoff.
 */
export function connectOwnerEventStream(opts: {
  onEvent: (event: SyncEvent) => void;
  onOpen: () => void;
  onError?: (error: Error) => void;
  url?: string;
  fetcher?: typeof fetch;
}): EventStreamHandle {
  const controller = new AbortController();
  const doFetch = opts.fetcher ?? fetch;
  let stopped = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  let resolveReconnect: (() => void) | undefined;

  function waitForReconnect(): Promise<void> {
    return new Promise((resolve) => {
      resolveReconnect = resolve;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = undefined;
        resolveReconnect = undefined;
        resolve();
      }, RECONNECT_BACKOFF_MS);
    });
  }

  async function loop(): Promise<void> {
    while (!stopped) {
      try {
        const headers: Record<string, string> = { Accept: "text/event-stream" };
        const path = opts.url ?? OWNER_EVENTS_STREAM_ENDPOINT;
        const res = await doFetch(path, { headers, signal: controller.signal, credentials: "same-origin" });
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(ownerEventStreamErrorMessage(path, res.status, res.statusText, text, parseRequestId(res.headers, text)));
        }
        if (!res.body) throw new Error(`Live-Stream nicht lesbar. Endpoint: ${path}. HTTP ${res.status} ${res.statusText || "OK"}.`);
        opts.onOpen();
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const parsed = parseSseChunk(buf);
          buf = parsed.rest;
          for (const e of parsed.events) opts.onEvent(e);
        }
      } catch (err) {
        if (stopped) return;
        opts.onError?.(err instanceof Error ? err : new Error(String(err)));
      }
      if (stopped) return;
      await waitForReconnect();
    }
  }

  void loop();
  return {
    close: () => {
      stopped = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = undefined;
      }
      resolveReconnect?.();
      resolveReconnect = undefined;
      controller.abort();
    },
  };
}
